import React from "react";

import { toTitleCase } from "@/util/common";
import StyledStatusPill from "./StatusPill.styled";

/**
 * Maps the display label to specific props that change the pill colors.
 *
 * See:
 *   - https://help.okta.com/oie/en-us/Content/Topics/users-groups-profiles/usgp-end-user-states.htm
 *   - https://citybase.atlassian.net/browse/PLFM-546
 *
 * @param {string} label - Text to be displayed within the pill
 * @returns {Object} - Predefined props that shift the pill color to a new color state
 */
function mapLabelToProps(label) {
  const propToLabelsMapping = {
    danger: ["deactivated", "suspended"],
    success: ["active"],
    warning: ["staged"],
  };

  const labelSearchValue = label.toLowerCase();

  const activeProp = Object.entries(propToLabelsMapping)
    .filter(([_prop, labels]) => labels.includes(labelSearchValue))
    .map(([prop, _labels]) => prop)
    .reduce((activeProp, prop) => prop || activeProp, "");

  return {
    danger: activeProp === "danger",
    success: activeProp === "success",
    warning: activeProp === "warning",
  };
}

/**
 * A pill component displaying the current Okta user status. Either a raw Okta status
 * string or a formatted status string can be provided to this component. When both
 * are present, the component defaults to the child string.
 *
 * @param {Object} props - The React component props
 * @param {string} [props.children] - Text to be displayed within the pill
 * @param {string} [props.status] - Text to be displayed within the pill
 * @returns {React.FC}
 */
const StatusPill = ({ children, status }) => {
  let label = "";
  if (typeof children === "string") label = children;
  else if (typeof status === "string") label = toTitleCase(status);

  const styleProps = mapLabelToProps(label);

  return <StyledStatusPill {...styleProps}>{label}</StyledStatusPill>;
};

export const __private__ = { mapLabelToProps };
export default StatusPill;
